import { useCallback, useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { Providers } from 'generated/graphql'
import useSearchParams from 'hooks/useSearchParams'
import { UserStore } from 'stores'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { DEFAULT_UPLOAD_DATE } from './use-brand-url-params.consts'
import type { IBrandUrlParams, IUseBrandUrlParamsReturn } from './use-brand-url-params.types'

export const useBrandUrlParams = (): IUseBrandUrlParamsReturn => {
  const history = useHistory()
  const location = useLocation()
  const { entityId, tab, panelId } = useParams<IBrandUrlParams>()
  const searchParams = useSearchParams()

  const providersKey = searchParams.getAll('providers').join(',')
  const uploadDateParam = searchParams.get('uploadDate') || DEFAULT_UPLOAD_DATE
  const storeProviders = useMemo(() => UserStore?.providers ?? [], [])

  const providers = useMemo(() => {
    const validProviders = Object.values(Providers)
    const providersArray = providersKey ? providersKey.split(',') : []
    return providersArray.length
      ? providersArray.filter((p): p is Providers => validProviders.includes(p as Providers))
      : storeProviders.filter((p): p is Providers => validProviders.includes(p as Providers))
  }, [providersKey, storeProviders])

  const baseUrl = useMemo(() => {
    return getRoute(PATHNAMES.WORKSPACE_BRAND_OVERVIEW, { panelId, entityId })
  }, [panelId, entityId])

  const tabLinkSearchParams = useMemo(() => {
    const params = new URLSearchParams()
    if (providersKey) {
      providersKey.split(',').forEach(provider => params.append('providers', provider))
    }
    if (uploadDateParam && uploadDateParam !== DEFAULT_UPLOAD_DATE) {
      params.set('uploadDate', uploadDateParam)
    }
    return params
  }, [providersKey, uploadDateParam])

  const updateProvidersUrl = useCallback(
    (newProviders: Providers[]) => {
      const params = new URLSearchParams(location.search)
      params.delete('providers')

      const allProvidersSelected = newProviders.length === storeProviders.length
      if (!allProvidersSelected) {
        newProviders.forEach(provider => params.append('providers', provider))
      }

      history.replace({ search: params.toString() })
    },
    [history, location.search, storeProviders.length]
  )

  const handleProvidersChange = updateProvidersUrl

  const shouldRedirectToOverview = !tab
  const redirectPath = `${baseUrl}/overview?${tabLinkSearchParams.toString()}`

  return {
    entityId,
    tab,
    providers,
    uploadDateParam,
    baseUrl,
    tabLinkSearchParams,
    shouldRedirectToOverview,
    redirectPath,
    handleProvidersChange,
  }
}
